// src/pages/ProjectDetail.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, adminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Edit2, X, Calendar, Building2, Globe, User,
  DollarSign, MapPin, FileText, Save, Plus, Trash2,
  Upload, Download, Users, ClipboardList, FolderOpen, Pencil, Check,
} from 'lucide-react';
import './TimesheetDetail.css';

const STATUT_META = {
  EN_COURS: { label: 'En cours', color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  ARCHIVE:  { label: 'Archivé',  color: '#8B8FA8', bg: 'rgba(139,143,168,0.12)' },
};

const ACTION_TYPES  = ['NOTE','APPEL','EMAIL','REUNION','RELANCE','AUTRE'];
const ACTION_STATUS = { A_FAIRE:{ label:'À faire', color:'#F59E0B' }, FAIT:{ label:'Fait', color:'#16A34A' }, ANNULE:{ label:'Annulé', color:'#8B8FA8' } };
const DOC_TYPES     = ['CONTRAT','BON_COMMANDE','FACTURE','RAPPORT','AUTRE'];
const DEVISES       = ['EUR','USD','GBP','DZD','TND','MAD'];
const RH_ADMIN      = ['SUPER_ADMIN','ADMIN','RH'];

const EMPTY_PR_FORM = {
  resource_id: '', poste: '', date_debut: '', date_fin: '',
  tjm: '', nb_jours_vendus: '', ca_ht: '', etat: '', type_prestation: '',
};
const EMPTY_FORM = {
  nom:'', statut:'EN_COURS', date_debut:'', date_fin:'',
  responsable_id:'', agence:'', client:'', devise:'EUR',
  adresse:'', code_postal:'', pays:'', commentaire:'',
  reference_interne:'', type_projet:'', societe_nom:'',
  contact_nom:'', contact_prenom:'', ville:'', pole:'',
  ca_ht:'', marge_ht:'', rentabilite:'',
  taux_change:'', taux_change_agence:'',
  etat_motif:'', motif_commentaire:'', besoin_reference_interne:'',
  societe_secteur:'',
};
const EMPTY_ACTION = { action_type:'NOTE', title:'', description:'', status:'FAIT' };

function fmt(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function fmtDt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
}
function fmtNum(val, decimals = 2) {
  if (val == null) return '—';
  return Number(val).toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
function PrInput({ value, onChange, type='text', placeholder='', style={} }) {
  return (
    <input type={type} className="form-control" value={value} onChange={onChange} placeholder={placeholder}
      style={{ fontSize:'0.75rem', padding:'4px 8px', ...style }}/>
  );
}
function InfoRow({ label, value, icon: Icon }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
      <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', letterSpacing:'0.04em', display:'flex', alignItems:'center', gap:4 }}>
        {Icon && <Icon size={10}/>}{label}
      </div>
      <div style={{ fontSize:'0.875rem', color: value ? 'var(--text)' : 'var(--text-faint)' }}>
        {value || '—'}
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = RH_ADMIN.includes(user?.role);

  const [project,   setProject]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [modal,     setModal]     = useState(false);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);

  // Prestations
  const [users,        setUsers]        = useState([]);
  const [prForm,       setPrForm]       = useState(EMPTY_PR_FORM);
  const [showPrForm,   setShowPrForm]   = useState(false);
  const [editingPrId,  setEditingPrId]  = useState(null);
  const [editPrForm,   setEditPrForm]   = useState(EMPTY_PR_FORM);
  const [savingPr,     setSavingPr]     = useState(false);

  // Actions
  const [actions,     setActions]     = useState([]);
  const [actionForm,  setActionForm]  = useState(EMPTY_ACTION);
  const [showActForm, setShowActForm] = useState(false);
  const [savingAct,   setSavingAct]   = useState(false);

  // Documents
  const [docs,      setDocs]      = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType,   setDocType]   = useState('AUTRE');
  const [docTitle,  setDocTitle]  = useState('');
  const fileRef = useRef();

  const loadProject = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsAPI.get(id);
      setProject(res.data);
    } catch {
      toast.error('Projet introuvable');
      navigate('/projects');
    } finally { setLoading(false); }
  }, [id, navigate]);

  const loadActions = useCallback(async () => {
    try { const r = await projectsAPI.listActions(id); setActions(r.data || []); } catch {}
  }, [id]);

  const loadDocs = useCallback(async () => {
    try { const r = await projectsAPI.listDocs(id); setDocs(r.data || []); } catch {}
  }, [id]);

  useEffect(() => {
    loadProject();
    loadActions();
    loadDocs();
    if (canEdit) {
      adminAPI.listUsers().then(r => setUsers(r.data || [])).catch(() => {});
    }
  }, [loadProject, loadActions, loadDocs, canEdit]);

  // ── Edit project ───────────────────────────────────────────────
  const openEdit = () => {
    if (!project) return;
    setForm({
      nom:                      project.nom || '',
      statut:                   project.statut || 'EN_COURS',
      date_debut:               project.date_debut || '',
      date_fin:                 project.date_fin || '',
      responsable_id:           project.responsable_id || '',
      agence:                   project.agence || '',
      client:                   project.client || '',
      devise:                   project.devise || 'EUR',
      adresse:                  project.adresse || '',
      code_postal:              project.code_postal || '',
      pays:                     project.pays || '',
      commentaire:              project.commentaire || '',
      reference_interne:        project.reference_interne || '',
      type_projet:              project.type_projet || '',
      societe_nom:              project.societe_nom || '',
      contact_nom:              project.contact_nom || '',
      contact_prenom:           project.contact_prenom || '',
      ville:                    project.ville || '',
      pole:                     project.pole || '',
      ca_ht:                    project.ca_ht != null ? String(project.ca_ht) : '',
      marge_ht:                 project.marge_ht != null ? String(project.marge_ht) : '',
      rentabilite:              project.rentabilite || '',
      taux_change:              project.taux_change != null ? String(project.taux_change) : '',
      taux_change_agence:       project.taux_change_agence != null ? String(project.taux_change_agence) : '',
      etat_motif:               project.etat_motif || '',
      motif_commentaire:        project.motif_commentaire || '',
      besoin_reference_interne: project.besoin_reference_interne || '',
      societe_secteur:          project.societe_secteur || '',
    });
    setModal(true);
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        responsable_id:     form.responsable_id || null,
        date_debut:         form.date_debut  || null,
        date_fin:           form.date_fin    || null,
        ca_ht:              form.ca_ht       ? parseFloat(form.ca_ht)       : null,
        marge_ht:           form.marge_ht    ? parseFloat(form.marge_ht)    : null,
        taux_change:        form.taux_change ? parseFloat(form.taux_change) : null,
        taux_change_agence: form.taux_change_agence ? parseFloat(form.taux_change_agence) : null,
        prestations: [],
      };
      await projectsAPI.update(id, payload);
      toast.success('Projet mis à jour');
      setModal(false);
      loadProject();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  // ── Prestations CRUD ───────────────────────────────────────────
  const prPayload = (f) => ({
    resource_id:     f.resource_id   || null,
    poste:           f.poste         || null,
    date_debut:      f.date_debut    || null,
    date_fin:        f.date_fin      || null,
    tjm:             f.tjm           ? parseFloat(f.tjm)           : null,
    nb_jours_vendus: f.nb_jours_vendus ? parseFloat(f.nb_jours_vendus) : null,
    ca_ht:           f.ca_ht         ? parseFloat(f.ca_ht)         : null,
    etat:            f.etat          || null,
    type_prestation: f.type_prestation || null,
  });

  const handleCreatePr = async () => {
    setSavingPr(true);
    try {
      await projectsAPI.createPrestation(id, prPayload(prForm));
      toast.success('Prestation ajoutée');
      setPrForm(EMPTY_PR_FORM);
      setShowPrForm(false);
      loadProject();
    } catch { toast.error('Erreur'); }
    finally { setSavingPr(false); }
  };

  const handleUpdatePr = async (pid) => {
    setSavingPr(true);
    try {
      await projectsAPI.updatePrestation(id, pid, prPayload(editPrForm));
      toast.success('Prestation mise à jour');
      setEditingPrId(null);
      loadProject();
    } catch { toast.error('Erreur'); }
    finally { setSavingPr(false); }
  };

  const handleDeletePr = async (pid) => {
    if (!window.confirm('Supprimer cette prestation ?')) return;
    try { await projectsAPI.deletePrestation(id, pid); toast.success('Supprimé'); loadProject(); }
    catch { toast.error('Erreur'); }
  };

  const startEditPr = (pr) => {
    setEditingPrId(pr.id);
    setEditPrForm({
      resource_id:     pr.resource_id    ? String(pr.resource_id) : '',
      poste:           pr.poste           || '',
      date_debut:      pr.date_debut      || '',
      date_fin:        pr.date_fin        || '',
      tjm:             pr.tjm != null     ? String(pr.tjm) : '',
      nb_jours_vendus: pr.nb_jours_vendus != null ? String(pr.nb_jours_vendus) : '',
      ca_ht:           pr.ca_ht != null   ? String(pr.ca_ht) : '',
      etat:            pr.etat            || '',
      type_prestation: pr.type_prestation || '',
    });
  };

  // ── Actions CRUD ───────────────────────────────────────────────
  const handleCreateAction = async () => {
    if (!actionForm.title.trim()) { toast.error('Titre requis'); return; }
    setSavingAct(true);
    try {
      await projectsAPI.createAction(id, actionForm);
      toast.success('Action ajoutée');
      setActionForm(EMPTY_ACTION);
      setShowActForm(false);
      loadActions();
    } catch { toast.error('Erreur'); }
    finally { setSavingAct(false); }
  };

  const handleDeleteAction = async (aid) => {
    if (!window.confirm('Supprimer cette action ?')) return;
    try { await projectsAPI.deleteAction(id, aid); loadActions(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };

  // ── Documents upload ───────────────────────────────────────────
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', docType);
      fd.append('title', docTitle || file.name);
      await projectsAPI.uploadDoc(id, fd);
      toast.success('Document ajouté');
      setDocTitle('');
      if (fileRef.current) fileRef.current.value = '';
      loadDocs();
    } catch { toast.error('Erreur upload'); }
    finally { setUploading(false); }
  };

  const handleDeleteDoc = async (did) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await projectsAPI.deleteDoc(id, did); loadDocs(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="loading-state"><div className="spinner"/></div>;
  if (!project) return null;

  const sm = STATUT_META[project.statut] || STATUT_META.EN_COURS;
  const tabs = [
    { key:'overview',    label:'Vue générale',                                       icon: ClipboardList },
    { key:'prestations', label:`Prestations (${(project.prestations||[]).length})`,  icon: Users },
    { key:'actions',     label:`Actions (${actions.length})`,                        icon: ClipboardList },
    { key:'documents',   label:`Documents (${docs.length})`,                         icon: FolderOpen },
  ];

  // Helper: label utilisateur
  const userLabel = (uid) => {
    const u = users.find(u => String(u.id) === String(uid));
    return u ? `${u.prenom || ''} ${u.nom || ''}`.trim() : uid;
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/projects')}>
              <ArrowLeft size={15}/> Retour
            </button>
            <span className="badge" style={{ color:sm.color, background:sm.bg, border:`1px solid ${sm.color}33`, fontSize:'0.8rem', padding:'4px 10px' }}>
              {sm.label}
            </span>
            {project.reference_interne && (
              <span style={{ fontSize:'0.8rem', color:'var(--text-faint)', fontFamily:'monospace' }}>#{project.reference_interne}</span>
            )}
          </div>
          <h1 style={{ margin:0 }}>{project.nom}</h1>
          {project.societe_nom && (
            <p style={{ marginTop:4, color:'var(--text-muted)', display:'flex', alignItems:'center', gap:6 }}>
              <Building2 size={14}/> {project.societe_nom}
              {project.type_projet && <span style={{ color:'var(--text-faint)' }}> · {project.type_projet}</span>}
            </p>
          )}
        </div>
        <div className="page-header-actions">
          {canEdit && (
            <button className="btn btn-primary" onClick={openEdit}><Edit2 size={15}/> Modifier</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, borderBottom:'2px solid var(--border)', marginBottom:20 }}>
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              background:'none', border:'none', cursor:'pointer',
              padding:'10px 18px', fontSize:'0.875rem', fontWeight:600,
              display:'flex', alignItems:'center', gap:6,
              borderBottom: activeTab === t.key ? '2px solid #FF4100' : '2px solid transparent',
              color: activeTab === t.key ? '#FF4100' : 'var(--text-muted)',
              marginBottom: -2,
            }}>
              <Icon size={14}/>{t.label}
            </button>
          );
        })}
      </div>

      {/* ── Overview ─────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 16px', fontSize:'0.875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Informations générales</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <InfoRow label="Client"      value={project.client}        icon={User}/>
              <InfoRow label="Agence"      value={project.agence}        icon={Building2}/>
              <InfoRow label="Pôle"        value={project.pole}/>
              <InfoRow label="Responsable" value={[project.responsable_prenom, project.responsable_nom].filter(Boolean).join(' ') || null} icon={User}/>
              <InfoRow label="Début"       value={project.date_debut ? fmt(project.date_debut) : null} icon={Calendar}/>
              <InfoRow label="Fin"         value={project.date_fin   ? fmt(project.date_fin)   : null} icon={Calendar}/>
              <InfoRow label="Devise"            value={project.devise}            icon={DollarSign}/>
              <InfoRow label="CA HT"             value={project.ca_ht    != null ? fmtNum(project.ca_ht)    + ' ' + (project.devise||'EUR') : null}/>
              <InfoRow label="Marge HT"          value={project.marge_ht != null ? fmtNum(project.marge_ht) + ' ' + (project.devise||'EUR') : null}/>
              <InfoRow label="Rentabilité"       value={project.rentabilite}/>
              <InfoRow label="Resp. Manager"     value={project.responsable_manager} icon={User}/>
              <InfoRow label="Date de création"  value={fmtDt(project.boond_created_at)}  icon={Calendar}/>
              <InfoRow label="Date de mise à jour" value={fmtDt(project.boond_updated_at)} icon={Calendar}/>
            </div>
          </div>

          <div className="card" style={{ padding:24 }}>
            <h3 style={{ margin:'0 0 16px', fontSize:'0.875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Contact & Société</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <InfoRow label="Société"  value={project.societe_nom}    icon={Building2}/>
              <InfoRow label="Secteur"  value={project.societe_secteur}/>
              <InfoRow label="Contact"  value={[project.contact_prenom, project.contact_nom].filter(Boolean).join(' ') || null} icon={User}/>
              <InfoRow label="Adresse"  value={project.adresse}        icon={MapPin}/>
              <InfoRow label="Ville"    value={project.ville}          icon={MapPin}/>
              <InfoRow label="CP"       value={project.code_postal}/>
              <InfoRow label="Pays"     value={project.pays}           icon={Globe}/>
            </div>
          </div>

          {(project.commentaire || project.etat_motif) && (
            <div className="card" style={{ padding:24, gridColumn:'1/-1' }}>
              <h3 style={{ margin:'0 0 12px', fontSize:'0.875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                <FileText size={13} style={{ marginRight:6 }}/>Commentaires
              </h3>
              {project.commentaire && <p style={{ fontSize:'0.875rem', color:'var(--text)', whiteSpace:'pre-line', margin:0 }}>{project.commentaire}</p>}
              {project.etat_motif && (
                <div style={{ marginTop:12 }}>
                  <div style={{ fontSize:'0.7rem', fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', marginBottom:4 }}>Motif d'état</div>
                  <p style={{ fontSize:'0.875rem', color:'var(--text-muted)', margin:0 }}>{project.etat_motif}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Prestations ──────────────────────────────────────────── */}
      {activeTab === 'prestations' && (
        <div className="card">
          <div style={{ padding:'20px 20px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <h3 style={{ margin:0, fontSize:'0.875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
              <Users size={13} style={{ marginRight:6 }}/>Ressources affectées ({(project.prestations||[]).length})
            </h3>
            {canEdit && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowPrForm(v => !v)}>
                <Plus size={14}/> Nouvelle prestation
              </button>
            )}
          </div>

          {/* Formulaire ajout */}
          {showPrForm && (
            <div style={{ padding:16, borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginBottom:10 }}>
                <div className="form-group">
                  <label className="form-label">Ressource</label>
                  <select className="form-control" style={{ fontSize:'0.8rem' }} value={prForm.resource_id}
                    onChange={e => setPrForm(f=>({...f, resource_id:e.target.value}))}>
                    <option value="">— Aucune —</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Poste</label>
                  <PrInput value={prForm.poste} onChange={e=>setPrForm(f=>({...f,poste:e.target.value}))} placeholder="Consultant senior…"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Début</label>
                  <PrInput type="date" value={prForm.date_debut} onChange={e=>setPrForm(f=>({...f,date_debut:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Fin</label>
                  <PrInput type="date" value={prForm.date_fin} onChange={e=>setPrForm(f=>({...f,date_fin:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">TJM ({project.devise||'EUR'})</label>
                  <PrInput type="number" value={prForm.tjm} onChange={e=>setPrForm(f=>({...f,tjm:e.target.value}))} placeholder="0"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Jours vendus</label>
                  <PrInput type="number" value={prForm.nb_jours_vendus} onChange={e=>setPrForm(f=>({...f,nb_jours_vendus:e.target.value}))} placeholder="0"/>
                </div>
                <div className="form-group">
                  <label className="form-label">CA HT</label>
                  <PrInput type="number" value={prForm.ca_ht} onChange={e=>setPrForm(f=>({...f,ca_ht:e.target.value}))} placeholder="0"/>
                </div>
                <div className="form-group">
                  <label className="form-label">État</label>
                  <PrInput value={prForm.etat} onChange={e=>setPrForm(f=>({...f,etat:e.target.value}))} placeholder="En cours…"/>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowPrForm(false)}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleCreatePr} disabled={savingPr}>
                  <Save size={13}/> Enregistrer
                </button>
              </div>
            </div>
          )}

          {(project.prestations||[]).length === 0 && !showPrForm ? (
            <div style={{ padding:40, textAlign:'center', color:'var(--text-faint)', fontSize:'0.875rem' }}>
              Aucune prestation — cliquez « Nouvelle prestation » pour en ajouter une
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ressource</th>
                    <th>Poste</th>
                    <th>Période</th>
                    <th>TJM</th>
                    <th>Jours vendus</th>
                    <th>CA HT</th>
                    <th>État</th>
                    <th>Resp. Ressource</th>
                    <th>Resp. RH</th>
                    <th>Resp. Projet</th>
                    <th>Ville projet</th>
                    <th>Adresse projet</th>
                    <th>Date de création</th>
                    <th>Date de mise à jour</th>
                    {canEdit && <th/>}
                  </tr>
                </thead>
                <tbody>
                  {(project.prestations||[]).map(pr => {
                    const isEditing = editingPrId === pr.id;
                    return (
                      <tr key={pr.id}>
                        <td>
                          {isEditing ? (
                            <select className="form-control" style={{ fontSize:'0.75rem', padding:'3px 6px' }}
                              value={editPrForm.resource_id}
                              onChange={e=>setEditPrForm(f=>({...f,resource_id:e.target.value}))}>
                              <option value="">— Aucune —</option>
                              {users.map(u=><option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
                            </select>
                          ) : (
                            <div>
                              <div style={{ fontWeight:600, fontSize:'0.875rem' }}>
                                {[pr.resource_prenom, pr.resource_nom].filter(Boolean).join(' ') || (
                                  <span style={{ color:'var(--text-faint)' }}>{pr.resource_reference || '—'}</span>
                                )}
                              </div>
                              {pr.reference_interne && (
                                <div style={{ fontSize:'0.7rem', color:'var(--text-faint)', fontFamily:'monospace' }}>#{pr.reference_interne}</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          {isEditing
                            ? <PrInput value={editPrForm.poste} onChange={e=>setEditPrForm(f=>({...f,poste:e.target.value}))} placeholder="Poste…"/>
                            : <span style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>{pr.poste || '—'}</span>}
                        </td>
                        <td>
                          {isEditing ? (
                            <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                              <PrInput type="date" value={editPrForm.date_debut} onChange={e=>setEditPrForm(f=>({...f,date_debut:e.target.value}))}/>
                              <PrInput type="date" value={editPrForm.date_fin}   onChange={e=>setEditPrForm(f=>({...f,date_fin:e.target.value}))}/>
                            </div>
                          ) : (
                            <div style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
                              {pr.date_debut && <div>{fmt(pr.date_debut)}</div>}
                              {pr.date_fin   && <div style={{ color:'var(--text-faint)' }}>→ {fmt(pr.date_fin)}</div>}
                              {!pr.date_debut && !pr.date_fin && '—'}
                            </div>
                          )}
                        </td>
                        <td>
                          {isEditing
                            ? <PrInput type="number" value={editPrForm.tjm} onChange={e=>setEditPrForm(f=>({...f,tjm:e.target.value}))} placeholder="0" style={{width:80}}/>
                            : <span style={{ fontSize:'0.8125rem' }}>{pr.tjm != null ? fmtNum(pr.tjm,0) + ' ' + (project.devise||'EUR') : '—'}</span>}
                        </td>
                        <td>
                          {isEditing
                            ? <PrInput type="number" value={editPrForm.nb_jours_vendus} onChange={e=>setEditPrForm(f=>({...f,nb_jours_vendus:e.target.value}))} placeholder="0" style={{width:70}}/>
                            : <span style={{ fontSize:'0.8125rem' }}>{pr.nb_jours_vendus != null ? fmtNum(pr.nb_jours_vendus,1) + ' j' : '—'}</span>}
                        </td>
                        <td>
                          {isEditing
                            ? <PrInput type="number" value={editPrForm.ca_ht} onChange={e=>setEditPrForm(f=>({...f,ca_ht:e.target.value}))} placeholder="0" style={{width:90}}/>
                            : <span style={{ fontSize:'0.8125rem' }}>{pr.ca_ht != null ? fmtNum(pr.ca_ht) : '—'}</span>}
                        </td>
                        <td>
                          {isEditing
                            ? <PrInput value={editPrForm.etat} onChange={e=>setEditPrForm(f=>({...f,etat:e.target.value}))} placeholder="En cours…"/>
                            : pr.etat ? (
                                <span style={{ fontSize:'0.75rem', padding:'3px 8px', borderRadius:6, background:'rgba(59,130,246,0.10)', color:'#3B82F6', border:'1px solid rgba(59,130,246,0.25)' }}>
                                  {pr.etat}
                                </span>
                              ) : '—'}
                        </td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{pr.resource_responsable_manager || '—'}</td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{pr.resource_responsable_rh || '—'}</td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{pr.projet_responsable_manager || '—'}</td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{pr.projet_ville || '—'}</td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-muted)', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={pr.projet_adresse||''}>{pr.projet_adresse || '—'}</td>
                        <td style={{ fontSize:'0.75rem', color:'var(--text-faint)', whiteSpace:'nowrap' }}>{fmtDt(pr.created_at)}</td>
                        <td style={{ fontSize:'0.75rem', color:'var(--text-faint)', whiteSpace:'nowrap' }}>{fmtDt(pr.updated_at)}</td>
                        {canEdit && (
                          <td>
                            <div style={{ display:'flex', gap:4 }}>
                              {isEditing ? (
                                <>
                                  <button className="ts-btn-remove" title="Sauvegarder" onClick={() => handleUpdatePr(pr.id)} disabled={savingPr}
                                    style={{ color:'#16A34A' }}>
                                    <Check size={13}/>
                                  </button>
                                  <button className="ts-btn-remove" title="Annuler" onClick={() => setEditingPrId(null)}>
                                    <X size={13}/>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button className="ts-btn-remove" title="Modifier" onClick={() => startEditPr(pr)}>
                                    <Pencil size={13}/>
                                  </button>
                                  <button className="ts-btn-remove" title="Supprimer" onClick={() => handleDeletePr(pr.id)}>
                                    <Trash2 size={13}/>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Actions ──────────────────────────────────────────────── */}
      {activeTab === 'actions' && (
        <div className="card">
          <div style={{ padding:'20px 20px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <h3 style={{ margin:0, fontSize:'0.875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
              Actions ({actions.length})
            </h3>
            {canEdit && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowActForm(v => !v)}>
                <Plus size={14}/> Nouvelle action
              </button>
            )}
          </div>

          {showActForm && (
            <div style={{ padding:20, borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)' }}>
              <div className="form-grid" style={{ marginBottom:12 }}>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-control" value={actionForm.action_type}
                    onChange={e=>setActionForm(f=>({...f,action_type:e.target.value}))}>
                    {ACTION_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Statut</label>
                  <select className="form-control" value={actionForm.status}
                    onChange={e=>setActionForm(f=>({...f,status:e.target.value}))}>
                    {Object.entries(ACTION_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Titre *</label>
                  <input className="form-control" value={actionForm.title}
                    onChange={e=>setActionForm(f=>({...f,title:e.target.value}))} placeholder="Titre de l'action"/>
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows={2} value={actionForm.description}
                    onChange={e=>setActionForm(f=>({...f,description:e.target.value}))} placeholder="Détails…" style={{ resize:'vertical' }}/>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowActForm(false)}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleCreateAction} disabled={savingAct || !actionForm.title.trim()}>
                  <Save size={13}/> Enregistrer
                </button>
              </div>
            </div>
          )}

          {actions.length === 0 ? (
            <div style={{ padding:40, textAlign:'center', color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucune action enregistrée</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Type</th><th>Titre</th><th>Description</th><th>Statut</th><th>Date</th>{canEdit&&<th/>}</tr></thead>
                <tbody>
                  {actions.map(a => {
                    const st = ACTION_STATUS[a.status] || ACTION_STATUS.FAIT;
                    return (
                      <tr key={a.id}>
                        <td><span style={{ fontSize:'0.75rem', fontFamily:'monospace', color:'var(--text-faint)' }}>{a.action_type}</span></td>
                        <td style={{ fontWeight:600, fontSize:'0.875rem' }}>{a.title}</td>
                        <td style={{ fontSize:'0.8125rem', color:'var(--text-muted)', maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.description||'—'}</td>
                        <td>
                          <span style={{ fontSize:'0.75rem', padding:'3px 8px', borderRadius:6, color:st.color, background:`${st.color}18`, border:`1px solid ${st.color}33` }}>
                            {st.label}
                          </span>
                        </td>
                        <td style={{ fontSize:'0.8rem', color:'var(--text-faint)' }}>
                          {new Date(a.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        {canEdit && (
                          <td>
                            <button className="ts-btn-remove" onClick={() => handleDeleteAction(a.id)} title="Supprimer">
                              <Trash2 size={13}/>
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Documents ────────────────────────────────────────────── */}
      {activeTab === 'documents' && (
        <div className="card">
          <div style={{ padding:'20px 20px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <h3 style={{ margin:0, fontSize:'0.875rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
              <FolderOpen size={13} style={{ marginRight:6 }}/>Documents ({docs.length})
            </h3>
          </div>

          {canEdit && (
            <div style={{ padding:16, borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <select className="form-control" style={{ width:160 }} value={docType} onChange={e=>setDocType(e.target.value)}>
                {DOC_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              <input className="form-control" style={{ flex:1, minWidth:180 }} placeholder="Titre du document (optionnel)"
                value={docTitle} onChange={e=>setDocTitle(e.target.value)}/>
              <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer', margin:0 }}>
                <Upload size={14}/> {uploading ? 'Envoi…' : 'Choisir un fichier'}
                <input ref={fileRef} type="file" style={{ display:'none' }} onChange={handleUpload} disabled={uploading}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"/>
              </label>
            </div>
          )}

          {docs.length === 0 ? (
            <div style={{ padding:40, textAlign:'center', color:'var(--text-faint)', fontSize:'0.875rem' }}>Aucun document</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Type</th><th>Titre</th><th>Fichier</th><th>Date</th><th/></tr></thead>
                <tbody>
                  {docs.map(d => (
                    <tr key={d.id}>
                      <td><span style={{ fontSize:'0.75rem', fontFamily:'monospace', color:'var(--text-faint)' }}>{d.document_type}</span></td>
                      <td style={{ fontWeight:600, fontSize:'0.875rem' }}>{d.title||d.file_name}</td>
                      <td>
                        {d.file_path ? (
                          <a href={`${window.location.origin.replace(':3000',':8000')}${d.file_path}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ color:'#3B82F6', fontSize:'0.8125rem', display:'flex', alignItems:'center', gap:4 }}>
                            <Download size={13}/>{d.file_name}
                          </a>
                        ) : <span style={{ color:'var(--text-faint)', fontSize:'0.8125rem' }}>{d.file_name||'—'}</span>}
                      </td>
                      <td style={{ fontSize:'0.8rem', color:'var(--text-faint)' }}>
                        {new Date(d.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td>
                        {canEdit && (
                          <button className="ts-btn-remove" onClick={() => handleDeleteDoc(d.id)} title="Supprimer">
                            <Trash2 size={13}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal édition projet */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-card" style={{ width:680, maxHeight:'90vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0 }}>Modifier le projet</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16}/></button>
            </div>

            <div className="form-grid">
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Nom du projet *</label>
                <input className="form-control" value={form.nom} onChange={set('nom')} placeholder="Nom du projet"/>
              </div>
              <div className="form-group">
                <label className="form-label">Référence interne</label>
                <input className="form-control" value={form.reference_interne} onChange={set('reference_interne')} placeholder="Ex: PRJ130"/>
              </div>
              <div className="form-group">
                <label className="form-label">Type de projet</label>
                <input className="form-control" value={form.type_projet} onChange={set('type_projet')} placeholder="Ex: Régie Directe"/>
              </div>
              <div className="form-group">
                <label className="form-label">Client</label>
                <input className="form-control" value={form.client} onChange={set('client')} placeholder="Nom du client"/>
              </div>
              <div className="form-group">
                <label className="form-label">Statut</label>
                <select className="form-control" value={form.statut} onChange={set('statut')}>
                  <option value="EN_COURS">En cours</option>
                  <option value="ARCHIVE">Archivé</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Société</label>
                <input className="form-control" value={form.societe_nom} onChange={set('societe_nom')} placeholder="Nom de la société"/>
              </div>
              <div className="form-group">
                <label className="form-label">Secteur d'activité</label>
                <input className="form-control" value={form.societe_secteur} onChange={set('societe_secteur')} placeholder="Secteur"/>
              </div>
              <div className="form-group">
                <label className="form-label">Contact — Nom</label>
                <input className="form-control" value={form.contact_nom} onChange={set('contact_nom')} placeholder="Nom du contact"/>
              </div>
              <div className="form-group">
                <label className="form-label">Contact — Prénom</label>
                <input className="form-control" value={form.contact_prenom} onChange={set('contact_prenom')} placeholder="Prénom du contact"/>
              </div>
              <div className="form-group">
                <label className="form-label">Date de début</label>
                <input type="date" className="form-control" value={form.date_debut} onChange={set('date_debut')}/>
              </div>
              <div className="form-group">
                <label className="form-label">Date de fin</label>
                <input type="date" className="form-control" value={form.date_fin} onChange={set('date_fin')}/>
              </div>
              <div className="form-group">
                <label className="form-label">Agence / Site</label>
                <select className="form-control" value={form.agence} onChange={set('agence')}>
                  <option value="">— Sélectionner —</option>
                  <option value="FRANCE">France</option>
                  <option value="BELGIQUE">Belgique</option>
                  <option value="ALGERIE">Algérie</option>
                  <option value="TUNISIE">Tunisie</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Pôle</label>
                <input className="form-control" value={form.pole} onChange={set('pole')} placeholder="Pôle"/>
              </div>
              <div className="form-group">
                <label className="form-label">Devise</label>
                <select className="form-control" value={form.devise} onChange={set('devise')}>
                  {DEVISES.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">CA HT</label>
                <input type="number" className="form-control" value={form.ca_ht} onChange={set('ca_ht')} placeholder="0.00"/>
              </div>
              <div className="form-group">
                <label className="form-label">Marge HT</label>
                <input type="number" className="form-control" value={form.marge_ht} onChange={set('marge_ht')} placeholder="0.00"/>
              </div>
              <div className="form-group">
                <label className="form-label">Rentabilité</label>
                <input className="form-control" value={form.rentabilite} onChange={set('rentabilite')} placeholder="Ex: 100,00%"/>
              </div>
              <div className="form-group">
                <label className="form-label">Adresse</label>
                <input className="form-control" value={form.adresse} onChange={set('adresse')} placeholder="Adresse"/>
              </div>
              <div className="form-group">
                <label className="form-label">Ville</label>
                <input className="form-control" value={form.ville} onChange={set('ville')} placeholder="Ville"/>
              </div>
              <div className="form-group">
                <label className="form-label">Code postal</label>
                <input className="form-control" value={form.code_postal} onChange={set('code_postal')} placeholder="75001"/>
              </div>
              <div className="form-group">
                <label className="form-label">Pays</label>
                <input className="form-control" value={form.pays} onChange={set('pays')} placeholder="Pays"/>
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Commentaire</label>
                <textarea className="form-control" rows={3} value={form.commentaire} onChange={set('commentaire')} placeholder="Notes, contexte…" style={{ resize:'vertical' }}/>
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Motif d'état</label>
                <input className="form-control" value={form.etat_motif} onChange={set('etat_motif')} placeholder="Motif"/>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20, borderTop:'1px solid var(--border)', paddingTop:16 }}>
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.nom.trim()}>
                <Save size={14}/> {saving ? 'Sauvegarde…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
